import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, Share2, Users } from "lucide-react";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/referral/me");
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = async () => {
    if (!data?.invite_url) return;
    try {
      await navigator.clipboard.writeText(data.invite_url);
      toast.success("Invite link copied");
    } catch (err) {
      toast.error("Clipboard unavailable — long-press to copy");
    }
  };

  const share = () => {
    if (!data?.invite_url) return;
    const text = `I've been using the Martingale Playbook — AI-graded picks + disciplined progressions. First 10 days are on me:`;
    if (navigator.share) {
      navigator.share({ title: "Martingale Playbook", text, url: data.invite_url }).catch(() => {});
      return;
    }
    // Fallback: Twitter/X intent
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
      data.invite_url
    )}`;
    window.open(tweet, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="border border-zinc-800 p-6" data-testid="referral-card">
      <div className="overline neon-text mb-3 flex items-center gap-2">
        <Users size={12} strokeWidth={1.5} /> INVITE A FRIEND
      </div>
      <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
        Share your link: they get <span className="neon-text">10 free days</span>{" "}
        (vs. the usual 3), and you get <span className="neon-text">+7 days</span> added to
        your trial/subscription for every friend who signs up.
      </p>
      {loading && !data ? (
        <div className="mono text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <div className="mono text-xs text-zinc-500 mb-1">YOUR CODE</div>
          <div className="font-display text-3xl neon-text mb-4" data-testid="referral-code">
            {data?.code || "—"}
          </div>
          <div className="mono text-xs text-zinc-500 mb-1">INVITE URL</div>
          <div
            className="mono text-sm text-zinc-300 border border-zinc-800 px-3 py-2 mb-4 overflow-x-auto whitespace-nowrap"
            data-testid="referral-url"
          >
            {data?.invite_url}
          </div>
          <div className="flex gap-2">
            <button
              onClick={copy}
              data-testid="referral-copy"
              className="flex-1 flex items-center justify-center gap-2 py-2 border border-zinc-700 text-zinc-300 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono text-xs uppercase tracking-widest"
            >
              <Copy size={12} strokeWidth={1.5} /> Copy
            </button>
            <button
              onClick={share}
              data-testid="referral-share"
              className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#00D4FF] text-black font-mono text-xs uppercase tracking-widest hover:brightness-110"
            >
              <Share2 size={12} strokeWidth={1.5} /> Share
            </button>
          </div>
          <div className="mt-4 mono text-xs text-zinc-500">
            Friends invited so far:{" "}
            <span className="neon-text" data-testid="referral-count">
              {data?.referral_count ?? 0}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
